/**
 * Minimal video-call panel. Asks the browser for a camera + mic stream,
 * opens a Cloudflare Calls session through our worker proxy, and renders
 * the local preview. Pulling remote participants requires the other peers'
 * session ids + track names — that wiring depends on a membership-aware
 * signalling channel that isn't in scope for this UI scaffold yet.
 *
 * In stub mode (no Calls credentials) the session id is a synthetic
 * `stub-...` value so the UI still demonstrates end-to-end plumbing.
 */
import { useRef, useState } from 'react';
import type { ChatClient } from '../client/api.js';
import { CallsClient } from '../client/calls.js';
import { VideoCall } from '../client/video-call.js';

export interface VideoCallPanelProps {
  readonly client: ChatClient;
  readonly tokenProvider: () => Promise<string> | string;
  readonly mediaFactory?: () => Promise<MediaStream>;
  readonly peerFactory?: () => RTCPeerConnection;
  /**
   * "strict" forces all media to flow over TURN/TLS on port 443 so the
   * network observer sees only generic-looking TLS, at the cost of a
   * small latency bump. "permissive" allows direct peer-to-peer flows
   * (UDP/DTLS) which is a distinct traffic shape but lower latency.
   *
   * Default is "strict" — traffic-posture hardening.
   */
  readonly transportMode?: 'strict' | 'permissive';
  /** Credentials for a TURN server. Cloudflare Calls provisions TURN dynamically;
   *  this override exists so tests + alternate SFUs can plug in their own. */
  readonly turnServer?: RTCIceServer;
}

function defaultMediaFactory(): Promise<MediaStream> {
  return navigator.mediaDevices.getUserMedia({ video: true, audio: true });
}

/**
 * Cloudflare's public TURN endpoint. `turns:` is TLS over TCP on 443, which
 * looks like ordinary HTTPS on the wire and survives restrictive corporate
 * firewalls. Production calls should fetch per-session credentials from the
 * Calls API and plug them in here.
 */
const CLOUDFLARE_TURN_TLS: RTCIceServer = {
  urls: ['turns:turn.cloudflare.com:443?transport=tcp'],
};

function buildPeerFactory(
  mode: 'strict' | 'permissive',
  turnServer: RTCIceServer,
): () => RTCPeerConnection {
  return () =>
    new RTCPeerConnection({
      iceServers: [turnServer],
      iceTransportPolicy: mode === 'strict' ? 'relay' : 'all',
    });
}

export function VideoCallPanel(props: VideoCallPanelProps) {
  const {
    client,
    tokenProvider,
    mediaFactory = defaultMediaFactory,
    transportMode = 'strict',
    turnServer = CLOUDFLARE_TURN_TLS,
    peerFactory = buildPeerFactory(transportMode, turnServer),
  } = props;
  const videoRef = useRef<HTMLVideoElement>(null);
  const callRef = useRef<VideoCall | undefined>(undefined);
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [status, setStatus] = useState<'idle' | 'starting' | 'live' | 'stopping' | 'error'>('idle');
  const [error, setError] = useState<string | undefined>();

  async function handleStart() {
    setStatus('starting');
    setError(undefined);
    try {
      const stream = await mediaFactory();
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      const calls = CallsClient.fromChatClient(client, tokenProvider);
      const peer = peerFactory();
      const call = new VideoCall({ calls, peerConnection: peer });
      const result = await call.start(stream);
      callRef.current = call;
      setSessionId(result.sessionId);
      setStatus('live');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'failed to start call');
    }
  }

  async function handleStop() {
    setStatus('stopping');
    try {
      await callRef.current?.stop();
    } finally {
      callRef.current = undefined;
      setSessionId(undefined);
      setStatus('idle');
      if (videoRef.current) videoRef.current.srcObject = null;
    }
  }

  const isLive = status === 'live';
  const isStarting = status === 'starting';
  const showPanel = isLive || isStarting;

  return (
    <section data-testid="video-call" className={`video-panel${showPanel ? ' live' : ' idle'}`}>
      {showPanel ? (
        <>
          {error && (
            <p data-testid="video-error" className="video-error">
              {error}
            </p>
          )}
          <video
            data-testid="local-video"
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className="video-preview"
          />
          <div className="video-status-row">
            <span data-testid="video-status" className={`badge ${isLive ? 'online' : 'searching'}`}>
              {status}
            </span>
            {sessionId && (
              <span data-testid="video-session" className="mono faint">
                {sessionId.slice(0, 18)}…
              </span>
            )}
            <span className="grow" />
            <button
              data-testid="stop-call"
              type="button"
              className="danger"
              onClick={() => {
                void handleStop();
              }}
            >
              end call
            </button>
          </div>
        </>
      ) : (
        <div className="video-idle-row">
          <span aria-hidden="true">📹</span>
          <span data-testid="video-status" className="faint">
            {status}
          </span>
          {error && (
            <span data-testid="video-error" className="faint" style={{ color: 'var(--danger)' }}>
              {error}
            </span>
          )}
          <span className="grow" />
          <button
            data-testid="start-call"
            type="button"
            className="ghost"
            disabled={isStarting}
            onClick={() => {
              void handleStart();
            }}
          >
            start call
          </button>
        </div>
      )}
    </section>
  );
}
